import { useEffect, useId, useRef } from 'react'
import type { InputHTMLAttributes, ReactNode } from 'react'
import Icon from '../media/Icon'

/**
 * Checkbox — a real `<input type="checkbox">` wearing EDIMI's box.
 *
 * WHY NOT A `<button role="checkbox">` LIKE THE COMPOSER'S
 * Because these sit in forms that are actually submitted. The order screen's two
 * consent boxes are the record that somebody agreed to waive a withdrawal right;
 * a div pretending to be a checkbox loses native form participation, loses the
 * browser's own "required" semantics, and — the part nobody notices until an
 * audit — loses the behaviour assistive tech users already know.
 *
 * So the input stays. It is moved off-screen rather than `display:none`, because
 * a hidden input is not focusable and the whole control would drop out of the tab
 * order. The visible box is a sibling `<span>` driven by `:checked` and
 * `:focus-visible` on that input, which is why the brand focus ring appears on
 * keyboard focus and not on a mouse click.
 *
 * The label is a real `<label htmlFor>` and it wraps the painted box together with
 * the sentence: the whole row is one click target, which for a consent row is not
 * a nicety — a 22px box is a hard tap on a phone, and a consent that is awkward to
 * give is a consent people give by accident. Until 2026-09-17 the box sat OUTSIDE
 * the label, so a tap on it did nothing at all; on /register at 390 px the only
 * toggling text was the word „Akceptuję”, the two links took the rest of the line.
 */

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** The clickable sentence next to the box. Accepts nodes so it can carry links. */
  label?: ReactNode
  /** Helper text under the row. */
  hint?: string
  /** Error text under the row; also marks the input aria-invalid. */
  error?: string
  /** Stan mieszany („część zaznaczona”, artboard 02): kreska zamiast ptaszka, aria-checked=mixed. */
  indeterminate?: boolean
}

export function Checkbox({ label, hint, error, indeterminate = false, id, className = '', ...rest }: CheckboxProps) {
  const auto = useId()
  const ref = useRef<HTMLInputElement>(null)
  // `indeterminate` jest właściwością DOM, nie atrybutem — stąd ref.
  useEffect(() => { if (ref.current) ref.current.indeterminate = indeterminate }, [indeterminate])
  const inputId = id ?? `cb-${auto}`
  const describedBy = [error ? `${inputId}-err` : null, hint ? `${inputId}-hint` : null]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={['ds-check', error && 'is-error', className].filter(Boolean).join(' ')}>
      <div className="ds-check-row">
        <input
          {...rest}
          ref={ref}
          id={inputId}
          type="checkbox"
          className="ds-check-input"
          aria-checked={indeterminate ? 'mixed' : undefined}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy || undefined}
        />
        {/* aria-hidden: the input above is the control. The span is paint, and it lives
            INSIDE the label so that a tap on the box reaches the input like a tap on the
            sentence does. */}
        <label className="ds-check-label" htmlFor={inputId}>
          <span className="ds-check-box" aria-hidden="true">
            <Icon name={indeterminate ? 'Minus' : 'Check'} size={14} strokeWidth={3} />
          </span>
          {label ? <span className="ds-check-text">{label}</span> : null}
        </label>
      </div>
      {error ? (
        <div className="ds-check-error" id={`${inputId}-err`} role="alert">
          {error}
        </div>
      ) : null}
      {hint ? (
        <div className="ds-check-hint" id={`${inputId}-hint`}>
          {hint}
        </div>
      ) : null}
    </div>
  )
}

export default Checkbox
